import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const JourneyContext = createContext();

// Seedling stages - Natural growth progression from roots to sacred tree
const SEEDLING_STAGES = [
  { week: 1, name: "Roots", emoji: "🫘", description: "Planting the seeds of change" },
  { week: 2, name: "Germinating", emoji: "🌰", description: "Stirring beneath the surface" },
  { week: 3, name: "Breaking Ground", emoji: "🌱", description: "Emerging into the light" },
  { week: 4, name: "Sprout", emoji: "🌿", description: "First leaves unfurling" },
  { week: 6, name: "Seedling", emoji: "☘️", description: "Growing stronger each day" },
  { week: 8, name: "Young Plant", emoji: "🪴", description: "Establishing your foundation" },
  { week: 12, name: "Flowering", emoji: "🌷", description: "Beginning to bloom" },
  { week: 16, name: "In Full Bloom", emoji: "🌸", description: "Radiating beauty" },
  { week: 20, name: "Fruit Bearing", emoji: "🍃", description: "Sharing your gifts" },
  { week: 26, name: "Young Tree", emoji: "🌴", description: "Standing tall" },
  { week: 36, name: "Mature Tree", emoji: "🌳", description: "Deeply rooted wisdom" },
  { week: 52, name: "Ancient Tree", emoji: "🌲", description: "A year of growth" },
  { week: 78, name: "Sacred Grove", emoji: "🏞️", description: "Your legacy flourishes" },
  { week: 104, name: "Forest Guardian", emoji: "🌍", description: "Two years of transformation" }
];

// Activity thresholds for growth boost
const ACTIVITY_LEVELS = {
  dormant: { min: 0, label: "Dormant", color: "gray" },
  resting: { min: 1, label: "Resting", color: "blue" },
  growing: { min: 5, label: "Growing", color: "green" },
  thriving: { min: 15, label: "Thriving", color: "emerald" },
  flourishing: { min: 30, label: "Flourishing", color: "violet" }
};

export const JourneyProvider = ({ children }) => {
  const { user } = useAuth();
  const [journeyData, setJourneyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadJourneyData();
    } else {
      setJourneyData(null);
      setLoading(false);
    }
  }, [user]);

  const loadJourneyData = () => {
    const storageKey = `wellness_journey_${user?.id || 'guest'}`;
    const saved = localStorage.getItem(storageKey);
    
    if (saved) {
      const data = JSON.parse(saved);
      // Recalculate current week based on start date
      const startDate = new Date(data.startDate);
      const now = new Date();
      const diffTime = Math.abs(now - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const currentWeek = Math.ceil(diffDays / 7) || 1;
      
      setJourneyData({
        ...data,
        currentWeek,
        daysOnJourney: diffDays
      });
    } else {
      // Initialize new journey
      const newJourney = {
        startDate: new Date().toISOString(),
        currentWeek: 1,
        daysOnJourney: 1,
        weeklyActivity: { 1: [] },
        totalActivities: 0,
        seedlingStage: 0,
        lastActivityDate: null
      };
      localStorage.setItem(storageKey, JSON.stringify(newJourney));
      setJourneyData(newJourney);
    }
    setLoading(false);
  };

  const saveJourneyData = (data) => {
    const storageKey = `wellness_journey_${user?.id || 'guest'}`;
    localStorage.setItem(storageKey, JSON.stringify(data));
    setJourneyData(data);
  };

  // Log an activity (journal entry, affirmation viewed, workout, etc.)
  const logActivity = (activityType, details = {}) => {
    if (!journeyData) return;

    const now = new Date();
    const activity = {
      type: activityType,
      timestamp: now.toISOString(),
      ...details
    };

    const weekKey = journeyData.currentWeek;
    const weeklyActivity = { ...journeyData.weeklyActivity };
    
    if (!weeklyActivity[weekKey]) {
      weeklyActivity[weekKey] = [];
    }
    weeklyActivity[weekKey].push(activity);

    const newData = {
      ...journeyData,
      weeklyActivity,
      totalActivities: journeyData.totalActivities + 1,
      lastActivityDate: now.toISOString()
    };

    saveJourneyData(newData);
    return activity;
  };

  // Get current week's activity count
  const getCurrentWeekActivityCount = () => {
    if (!journeyData) return 0;
    const weekActivities = journeyData.weeklyActivity[journeyData.currentWeek] || [];
    return weekActivities.length;
  };

  // Get activity level based on current week's activities
  const getActivityLevel = () => {
    const count = getCurrentWeekActivityCount();
    if (count >= 30) return ACTIVITY_LEVELS.flourishing;
    if (count >= 15) return ACTIVITY_LEVELS.thriving;
    if (count >= 5) return ACTIVITY_LEVELS.growing;
    if (count >= 1) return ACTIVITY_LEVELS.resting;
    return ACTIVITY_LEVELS.dormant;
  };

  // Get seedling stage based on weeks + activity bonus
  const getSeedlingStage = () => {
    if (!journeyData) return SEEDLING_STAGES[0];
    
    const week = journeyData.currentWeek;
    const activityBonus = Math.floor(journeyData.totalActivities / 50); // Bonus week for every 50 activities
    const effectiveWeek = week + activityBonus;
    
    // Find the highest stage the user qualifies for
    let stage = SEEDLING_STAGES[0];
    for (const s of SEEDLING_STAGES) {
      if (effectiveWeek >= s.week) {
        stage = s;
      }
    }
    return stage;
  };

  // Get weekly summary
  const getWeeklySummary = (weekNumber) => {
    if (!journeyData) return null;
    const activities = journeyData.weeklyActivity[weekNumber] || [];
    
    const summary = {
      week: weekNumber,
      totalActivities: activities.length,
      breakdown: {}
    };

    activities.forEach(a => {
      summary.breakdown[a.type] = (summary.breakdown[a.type] || 0) + 1;
    });

    return summary;
  };

  // Get journey week label (e.g., "Week 4 of Your Journey")
  const getWeekLabel = () => {
    if (!journeyData) return "Week 1";
    return `Week ${journeyData.currentWeek}`;
  };

  // Get motivational message based on journey stage
  const getJourneyMessage = () => {
    if (!journeyData) return "Begin your wellness journey today!";
    
    const week = journeyData.currentWeek;
    const activityLevel = getActivityLevel();
    
    const messages = {
      1: "Every journey begins with a single step. You're planting seeds of transformation! 🌱",
      2: "Your dedication is showing! Keep nurturing your growth. 🌿",
      3: "Three weeks of commitment! Your roots are growing deeper. ☘️",
      4: "One month strong! You're building lasting habits. 🪴",
      5: "Your consistency is inspiring! The universe sees your effort. ✨",
      6: "Six weeks of growth! You're ready to bloom. 🌷",
      8: "Two months! Your transformation is becoming visible. 🌸",
      12: "Three months of dedication! You're flourishing beautifully. 🌺",
      16: "Four months! You've become deeply rooted in wellness. 🌳",
      24: "Six months! Your wisdom grows with each passing day. 🌲",
      52: "One year! You are a living testament to transformation. 🏞️"
    };

    // Find the closest message
    let message = messages[1];
    for (const [weekNum, msg] of Object.entries(messages)) {
      if (week >= parseInt(weekNum)) {
        message = msg;
      }
    }

    // Add activity-based encouragement
    if (activityLevel.label === "Dormant") {
      message += "\n\n💫 Your seedling awaits your return. Even one small action today will help it grow!";
    } else if (activityLevel.label === "Flourishing") {
      message += "\n\n🌟 You're absolutely flourishing this week! Your energy is radiant!";
    }

    return message;
  };

  const value = {
    journeyData,
    loading,
    logActivity,
    getCurrentWeekActivityCount,
    getActivityLevel,
    getSeedlingStage,
    getWeeklySummary,
    getWeekLabel,
    getJourneyMessage,
    SEEDLING_STAGES,
    ACTIVITY_LEVELS
  };

  return (
    <JourneyContext.Provider value={value}>
      {children}
    </JourneyContext.Provider>
  );
};

export const useJourney = () => {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourney must be used within a JourneyProvider');
  }
  return context;
};

export default JourneyContext;
